import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { PERMISSIONS } from '@lims/permissions';
import {
  ALL_ATTEMPT_ATTACHMENT_PURPOSES,
  RESULT_TEMPLATE,
  T96_GRADING_FRACTIONS,
  templateEquipmentRoles,
} from '@lims/shared-types';
import { ApiError } from '../../api/client.js';
import {
  attemptsApi,
  equipmentApi,
  type Attempt,
} from '../../api/endpoints.js';
import { useAuth } from '../../app/auth-context.js';
import { useLaboratoryTimeZone } from '../../app/laboratory-time-zone.js';
import {
  ActionBar,
  ActionButton,
  Alert,
  DateField,
  DateTimeField,
  Dialog,
  ErrorState,
  FileDownloadButton,
  FileUploadField,
  LoadingState,
  MarkCaution,
  MarkInTolerance,
  Panel,
  ReadOnlyField,
  SelectField,
  StatusBadge,
  TextAreaField,
  useToast,
  fieldGridClass,
} from '../../components/ui/index.js';
import { ErrorNotice } from '../../components/ErrorNotice.js';
import { formatBytes, formatDateTime } from '../../components/format.js';
import { currentLocale } from '../../i18n/index.js';
import { D2216Panel, type StructuredFormHandle } from './D2216Form.js';
import { LosAngelesAbrasionPanel } from './LosAngelesAbrasionForm.js';
import { SpecificGravityPanel, specificGravityFormFor } from './SpecificGravityForm.js';
import { ReadinessBanner } from './ReadinessBanner.js';
import { BeforeSubmitPanel, BenchLayout } from './bench-parts.js';
import {
  equipmentName,
  formatCalendarDate,
  testName,
  toneForAttemptStatus,
} from './status.js';

/**
 * THE GENERAL RESULT ENTRY FORM — module C6, requirements p.23.
 *
 * ===========================================================================
 * READ CLAUDE.md SECTION 4 BEFORE ADDING ANY FIELD TO THIS PAGE.
 * ===========================================================================
 *
 * This form carries exactly: a general result summary in Arabic and in
 * English, general notes, the execution dates, the equipment used, and
 * attached files — under a prominent readiness banner. There is NO reading,
 * unit, formula, calculated value, rounding, limit, pass/fail, chart or Excel
 * mapping here, for any test, and a test in `web` asserts none renders.
 *
 * ATTACHING A WORKSHEET IS NOT THE SYSTEM READING IT. The attachment panel
 * says so beside the upload, every time.
 *
 * A DRAFT IS NOT A RESULT. The preview is marked as an unissued draft, and a
 * submitted attempt is marked as submitted for review — which is not review,
 * not approval, and not a report.
 */
export function AttemptPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const { can, user } = useAuth();
  const timeZone = useLaboratoryTimeZone();
  const { show } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const locale = currentLocale();

  const attempt = useQuery({
    queryKey: ['attempt', id],
    queryFn: () => attemptsApi.get(id as string),
  });
  // The specialised form's current values, read by Submit (D249), and the
  // last refused submission, whose fields the form marks.
  const structuredForm = useRef<StructuredFormHandle | null>(null);
  const [submitError, setSubmitError] = useState<unknown>(null);

  const record = attempt.data;
  const isEditable =
    record !== undefined &&
    record.status === 'draft' &&
    record.performedBy.id === user?.id &&
    can(PERMISSIONS.RESULTS_ENTER);

  if (attempt.isLoading) return <LoadingState label={t('common.loading')} />;
  if (attempt.error) {
    return <ErrorState error={attempt.error} onRetry={() => void attempt.refetch()} />;
  }
  if (!record) return null;

  const refresh = async (next?: Attempt) => {
    if (next) queryClient.setQueryData(['attempt', id], next);
    await queryClient.invalidateQueries({ queryKey: ['attempt', id] });
    await queryClient.invalidateQueries({ queryKey: ['assignment', record.assignmentId] });
    await queryClient.invalidateQueries({ queryKey: ['my-tasks'] });
  };

  const structuredProps = {
    attempt: record,
    isEditable,
    onSaved: async (next: Attempt) => {
      setSubmitError(null);
      await refresh(next);
    },
    timeZone,
    handle: structuredForm,
    submitError,
    // D2216, T85 and T96 name their instruments' roles; the asphalt workbook
    // names none, so its instruments come from the register without one (D263).
    equipment: (
      <EquipmentPanel
        attempt={record}
        isEditable={isEditable}
        onChanged={refresh}
        {...(templateEquipmentRoles(record.resultPath).length > 0
          ? { roles: templateEquipmentRoles(record.resultPath) }
          : {})}
      />
    ),
    attachments: <AttachmentsPanel attempt={record} isEditable={isEditable} onChanged={refresh} />,
    ...(isEditable
      ? {
          submitAction: (
            <SubmitBar
              attempt={record}
              submitWith={() => attemptsApi.submit(record.id, structuredForm.current?.currentPayload() ?? null)}
              onRefused={setSubmitError}
              onSubmitted={async (next) => {
                await refresh(next);
                show({ tone: 'success', message: t('attempt.submittedNotice') });
              }}
            />
          ),
        }
      : {}),
  };
  const specificGravity = specificGravityFormFor(record.resultPath);
  const general = record.generalResult;

  return (
    <>
      <AttemptIdentity
        attempt={record}
        testLabel={testName(record.assignment.test, locale)}
        onBack={() => navigate(`/work/${record.assignmentId}`)}
      />

      <div className="flex flex-col gap-gutter">
        <ReadinessBanner readiness={record.assignment.test.templateReadiness} />

        {record.status === 'draft' ? (
          <Alert tone="warning" title={t('attempt.draftTitle')} description={t('attempt.draftBody')} />
        ) : record.status === 'submitted' ? (
          <Alert tone="info" title={t('attempt.submittedTitle')} description={t('attempt.submittedBody')} />
        ) : (
          <Alert
            tone="warning"
            title={t('attempt.discontinuedTitle')}
            description={t('attempt.discontinuedBecause', { reason: record.discontinuedReason ?? '' })}
          />
        )}
        {record.isRetest && record.retestReason ? (
          <Alert tone="info" title={t('attempt.retestTitle')} description={record.retestReason} />
        ) : null}

        {record.resultPath === 'general' ? (
          <BenchLayout
            main={
              <>
                <GeneralResultPanel
                  attempt={record}
                  isEditable={isEditable}
                  onSaved={refresh}
                  timeZone={timeZone}
                />
                <EquipmentPanel attempt={record} isEditable={isEditable} onChanged={refresh} />
                <AttachmentsPanel attempt={record} isEditable={isEditable} onChanged={refresh} />
                <DraftPreview attempt={record} />
              </>
            }
            side={
              // Read from the SAVED draft: the general path submits what was
              // last saved, so the list says what the submission would carry.
              <BeforeSubmitPanel
                checks={[
                  { key: 'summary', ok: Boolean(general.summaryAr || general.summaryEn), label: t('attempt.gap.summary') },
                  { key: 'started', ok: Boolean(general.executionStartedAt), label: t('attempt.gap.executionStartedAt') },
                  { key: 'completed', ok: Boolean(general.executionCompletedAt), label: t('attempt.gap.executionCompletedAt') },
                ]}
                {...(isEditable
                  ? {
                      actions: (
                        <>
                          <p className="basis-full text-caption text-default-600">{t('bench.generalSavedNote')}</p>
                          <span className="flex-1" />
                          <SubmitBar
                            attempt={record}
                            onSubmitted={async (next) => {
                              await refresh(next);
                              show({ tone: 'success', message: t('attempt.submittedNotice') });
                            }}
                          />
                        </>
                      ),
                    }
                  : {})}
              />
            }
          />
        ) : specificGravity ? (
          // The specialised path: only for an attempt that STARTED on a ready
          // item naming an implemented template (D239).
          <SpecificGravityPanel config={specificGravity} {...structuredProps} />
        ) : record.resultPath === RESULT_TEMPLATE.AASHTO_T96_V1 ? (
          <LosAngelesAbrasionPanel {...structuredProps} />
        ) : (
          <D2216Panel {...structuredProps} />
        )}
      </div>
    </>
  );
}

/**
 * The bench's identity line: the test code and the sample number, both
 * left-to-right technical values, the attempt's state in a badge, then the
 * test name, the technician and the attempt number.
 */
function AttemptIdentity({
  attempt,
  testLabel,
  onBack,
}: {
  readonly attempt: Attempt;
  readonly testLabel: string;
  readonly onBack: () => void;
}) {
  const { t } = useTranslation();
  const code = attempt.assignment.test.code;
  return (
    <header className="mb-gutter flex flex-wrap items-start gap-stack">
      <div className="min-w-0 grow basis-64">
        <div className="flex flex-wrap items-center gap-x-inline gap-y-1">
          {code ? (
            <>
              <span className="measure text-title font-bold text-default-900" dir="ltr">
                {code}
              </span>
              <span className="text-title text-default-600" aria-hidden="true">
                ·
              </span>
            </>
          ) : null}
          <Link
            to={`/samples/${attempt.assignment.sample.id}`}
            className="measure text-title font-bold text-default-900 hover:underline"
            dir="ltr"
          >
            {attempt.assignment.sample.sampleNumber}
            {attempt.assignment.pieceCode ? ` / ${attempt.assignment.pieceCode}` : ''}
          </Link>
          <StatusBadge label={t(`attemptStatus.${attempt.status}`)} tone={toneForAttemptStatus(attempt.status)} />
        </div>
        <p className="mt-0.5 text-body text-default-700">
          <bdi>{testLabel}</bdi>
          {' · '}
          <bdi>{t('attempt.performedBy', { name: attempt.performedBy.displayName ?? '' })}</bdi>
          {' · '}
          <bdi>{t('attempt.numbered', { number: attempt.attemptNumber })}</bdi>
        </p>
      </div>
      <ActionButton priority="tertiary" onPress={onBack}>
        {t('common.back')}
      </ActionButton>
    </header>
  );
}

// ---------------------------------------------------------------------------
// The general result — five things a person writes, and nothing else
// ---------------------------------------------------------------------------

export function GeneralResultPanel({
  attempt,
  isEditable,
  onSaved,
  timeZone,
}: {
  readonly attempt: Attempt;
  readonly isEditable: boolean;
  readonly onSaved: (next: Attempt) => Promise<void>;
  /** The laboratory's clock, on which the execution times are read. */
  readonly timeZone: string | null;
}) {
  const { t } = useTranslation();
  const { show } = useToast();
  const result = attempt.generalResult;

  const [summaryAr, setSummaryAr] = useState(result.summaryAr ?? '');
  const [summaryEn, setSummaryEn] = useState(result.summaryEn ?? '');
  const [generalNotes, setGeneralNotes] = useState(result.generalNotes ?? '');
  const [startedAt, setStartedAt] = useState<string | null>(result.executionStartedAt);
  const [completedAt, setCompletedAt] = useState<string | null>(result.executionCompletedAt);

  useEffect(() => {
    setSummaryAr(result.summaryAr ?? '');
    setSummaryEn(result.summaryEn ?? '');
    setGeneralNotes(result.generalNotes ?? '');
    setStartedAt(result.executionStartedAt);
    setCompletedAt(result.executionCompletedAt);
  }, [result.summaryAr, result.summaryEn, result.generalNotes, result.executionStartedAt, result.executionCompletedAt]);

  // An empty field is sent as null: absent, never zero and never "".
  const orNull = (value: string) => (value.trim() === '' ? null : value.trim());

  const save = useMutation({
    mutationFn: () =>
      attemptsApi.save(attempt.id, {
        summaryAr: orNull(summaryAr),
        summaryEn: orNull(summaryEn),
        generalNotes: orNull(generalNotes),
        executionStartedAt: startedAt,
        executionCompletedAt: completedAt,
      }),
    onSuccess: async (next) => {
      await onSaved(next);
      show({ tone: 'success', message: t('attempt.draftSaved') });
    },
  });

  return (
    <Panel title={t('attempt.generalResultTitle')} description={t('attempt.generalResultHint')}>
      <div className="flex flex-col gap-stack" data-testid="general-result-form">
        <div className="grid grid-cols-1 gap-stack lg:grid-cols-2">
          <div dir="rtl" lang="ar">
            <TextAreaField
              label={t('attempt.summaryArLabel')}
              value={summaryAr}
              onChange={setSummaryAr}
              isReadOnly={!isEditable}
              rows={5}
            />
          </div>
          <div dir="ltr" lang="en">
            <TextAreaField
              label={t('attempt.summaryEnLabel')}
              value={summaryEn}
              onChange={setSummaryEn}
              isReadOnly={!isEditable}
              rows={5}
            />
          </div>
        </div>
        <TextAreaField
          label={t('attempt.generalNotesLabel')}
          value={generalNotes}
          onChange={setGeneralNotes}
          isReadOnly={!isEditable}
          rows={3}
        />
        <div className="grid grid-cols-1 gap-stack sm:grid-cols-2">
          <DateTimeField
            label={t('attempt.executionStartedLabel')}
            value={startedAt}
            onChange={setStartedAt}
            timeZone={timeZone}
            isReadOnly={!isEditable}
          />
          <DateTimeField
            label={t('attempt.executionCompletedLabel')}
            value={completedAt}
            onChange={setCompletedAt}
            timeZone={timeZone}
            isReadOnly={!isEditable}
          />
        </div>
        <p className="text-caption text-default-600">{t('attempt.emptyIsAbsent')}</p>
        {isEditable ? (
          <ActionBar>
            <ActionButton priority="secondary" isLoading={save.isPending} onPress={() => save.mutate()}>
              {t('attempt.saveDraft')}
            </ActionButton>
          </ActionBar>
        ) : null}
        {save.error ? <ErrorNotice error={save.error} /> : null}
      </div>
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Equipment used, with calibration validation
// ---------------------------------------------------------------------------

function EquipmentPanel({
  attempt,
  isEditable,
  onChanged,
  roles,
}: {
  readonly attempt: Attempt;
  readonly isEditable: boolean;
  readonly onChanged: (next: Attempt) => Promise<void>;
  /** A specialised template's equipment roles (D243); none on the general path. */
  readonly roles?: readonly string[];
}) {
  const { t } = useTranslation();
  const { can } = useAuth();
  const locale = currentLocale();

  const [role, setRole] = useState<string | null>(null);
  const [equipmentId, setEquipmentId] = useState<string | null>(null);
  const [usedOn, setUsedOn] = useState<string | null>(null);
  const [overrideReason, setOverrideReason] = useState('');

  const register = useQuery({
    queryKey: ['equipment', 'for-attempt'],
    queryFn: () => equipmentApi.list({ pageSize: 100, status: 'in_service' }),
    enabled: isEditable && can(PERMISSIONS.EQUIPMENT_VIEW),
  });
  const suspended = useQuery({
    queryKey: ['equipment', 'for-attempt', 'suspended'],
    queryFn: () => equipmentApi.list({ pageSize: 100, status: 'suspended' }),
    enabled: isEditable && can(PERMISSIONS.EQUIPMENT_VIEW),
  });

  const items = [...(register.data?.data ?? []), ...(suspended.data?.data ?? [])];
  const chosen = items.find((item) => item.id === equipmentId);
  const mayOverride = can(PERMISSIONS.EQUIPMENT_OVERRIDE_CALIBRATION);

  const record = useMutation({
    mutationFn: () =>
      attemptsApi.recordEquipment(attempt.id, {
        equipmentId,
        usedOn,
        ...(role ? { role } : {}),
        ...(overrideReason.trim() ? { overrideReason: overrideReason.trim() } : {}),
      }),
    onSuccess: async (next) => {
      await onChanged(next);
      setEquipmentId(null);
      setOverrideReason('');
    },
  });
  const remove = useMutation({
    mutationFn: (usageId: string) => attemptsApi.removeEquipment(attempt.id, usageId, {}),
    onSuccess: onChanged,
  });

  // The backend's refusal, by code, so the reason is shown in the reader's
  // language rather than as prose.
  const refusal =
    record.error instanceof ApiError
      ? record.error.details.find((detail) => detail.field === 'equipmentId')?.code
      : undefined;

  return (
    <Panel
      title={t('attempt.equipmentTitle')}
      description={roles && roles.length > 0 ? t('bench.slotsHint') : t('attempt.equipmentHint')}
    >
      {/* One slot per instrument the template names; anything else recorded,
          and a removed use, stays listed — never hidden. */}
      {(() => {
        const live = attempt.equipment.filter((use) => !use.removedAt);
        const removed = attempt.equipment.filter((use) => use.removedAt);
        const named = roles ?? [];
        const others = live.filter((use) => !use.role || !named.includes(use.role));
        const line = (use: Attempt['equipment'][number]) => (
          <div key={use.id} className={`flex flex-wrap items-start gap-inline ${use.removedAt ? 'opacity-60' : ''}`}>
            <div className="min-w-0 flex-1">
              <p className="text-body font-medium text-default-900">
                {use.role && (!named.includes(use.role) || use.removedAt) ? (
                  <span className="me-2 text-caption font-medium text-default-700" data-testid="equipment-role">
                    {t(`equipmentRole.${use.role}`, { defaultValue: use.role })}
                  </span>
                ) : null}
                <span className="measure" dir="ltr">
                  {use.equipmentCode}
                </span>{' '}
                {equipmentName(use)}
              </p>
              <p className="text-caption text-default-600">
                <bdi>{t('attempt.usedOn', { date: formatCalendarDate(use.usedOn, locale) })}</bdi>
                {' · '}
                <bdi>
                  {use.calibrationRequiredAtUse
                    ? use.calibrationValidUntilAtUse
                      ? t('equipment.validUntil', { date: formatCalendarDate(use.calibrationValidUntilAtUse, locale) })
                      : t('equipment.noCalibrationRecorded')
                    : t('equipment.calibrationNotRequired')}
                </bdi>
              </p>
              {use.blockReason ? (
                <p className="text-caption font-medium text-danger-700">
                  {t('attempt.usedByException', {
                    reason: t(`equipmentBlock.${use.blockReason}`),
                    note: use.overrideReason ?? '',
                  })}
                </p>
              ) : null}
              {use.removedAt ? (
                <p className="text-caption text-default-600">
                  {t('attempt.removedBy', { name: use.removedBy?.displayName ?? '' })}
                </p>
              ) : null}
            </div>
            {isEditable && !use.removedAt ? (
              <ActionButton priority="tertiary" size="sm" isLoading={remove.isPending} onPress={() => remove.mutate(use.id)}>
                {t('common.remove')}
              </ActionButton>
            ) : null}
          </div>
        );
        return (
          <div className="flex flex-col gap-stack">
            {named.length > 0 || live.length > 0 ? (
              <ul className="grid grid-cols-1 gap-stack sm:grid-cols-2">
                {named.map((slot) => {
                  const uses = live.filter((use) => use.role === slot);
                  const usable = uses.length > 0 && uses.every((use) => !use.blockReason);
                  return (
                    <li
                      key={slot}
                      className={`flex min-w-0 flex-col gap-2 rounded-large border p-3 ${
                        uses.length === 0
                          ? 'border-dashed border-warning-300 bg-warning-50'
                          : usable
                            ? 'border-default-200'
                            : 'border-danger-300'
                      }`}
                      data-testid="equipment-slot"
                    >
                      <p className="flex items-center gap-1.5 text-caption font-semibold text-default-800">
                        <span data-testid="equipment-role">{t(`equipmentRole.${slot}`, { defaultValue: slot })}</span>
                        <span className="flex-1" />
                        {uses.length === 0 ? (
                          <span className="text-warning-700">{t('bench.slotEmpty')}</span>
                        ) : usable ? (
                          <span className="inline-flex text-success-700">
                            <MarkInTolerance size={13} />
                            <span className="sr-only">{t('bench.slotUsable')}</span>
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-danger-700">
                            <MarkCaution size={13} />
                            {t('bench.slotException')}
                          </span>
                        )}
                      </p>
                      {uses.map(line)}
                    </li>
                  );
                })}
                {others.map((use) => (
                  <li key={use.id} className="flex min-w-0 flex-col gap-2 rounded-large border border-default-200 p-3">
                    {named.length > 0 ? (
                      <p className="text-caption font-semibold text-default-800">{t('bench.slotAlsoUsed')}</p>
                    ) : null}
                    {line(use)}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-body text-default-600">{t('attempt.noEquipment')}</p>
            )}
            {removed.length > 0 ? (
              <div className="flex flex-col gap-2 border-t border-default-100 pt-stack">
                <p className="text-caption font-semibold text-default-700">{t('bench.removedUses')}</p>
                {removed.map(line)}
              </div>
            ) : null}
          </div>
        );
      })()}

      {isEditable ? (
        <div className="mt-stack flex flex-col gap-stack border-t border-default-100 pt-stack">
          <div className={fieldGridClass(2)}>
            {roles && roles.length > 0 ? (
              <SelectField
                label={t('equipmentRole.label')}
                value={role}
                onChange={setRole}
                placeholder={t('equipmentRole.placeholder')}
                isRequired
                options={roles.map((value) => ({ value, label: t(`equipmentRole.${value}`) }))}
              />
            ) : null}
            <SelectField
              label={t('attempt.equipmentLabel')}
              value={equipmentId}
              onChange={setEquipmentId}
              placeholder={t('attempt.equipmentPlaceholder')}
              options={items.map((item) => ({
                value: item.id,
                label: `${item.equipmentCode} ${equipmentName(item)}`,
                description: item.usability.usable
                  ? t('equipment.usableToday')
                  : t(`equipmentBlock.${item.usability.blockReason ?? 'suspended'}`),
              }))}
            />
            <DateField label={t('attempt.usedOnLabel')} value={usedOn} onChange={setUsedOn} isRequired />
          </div>
          {chosen && !chosen.usability.usable ? (
            <Alert
              tone="danger"
              title={t(`equipmentBlock.${chosen.usability.blockReason ?? 'suspended'}`)}
              description={mayOverride ? t('attempt.overrideAvailable') : t('attempt.overrideUnavailable')}
            />
          ) : null}
          {chosen && !chosen.usability.usable && mayOverride ? (
            <TextAreaField
              label={t('attempt.overrideReasonLabel')}
              value={overrideReason}
              onChange={setOverrideReason}
              isRequired
              rows={2}
            />
          ) : null}
          <ActionBar>
            <ActionButton
              priority="secondary"
              isDisabled={!equipmentId || !usedOn || (Boolean(roles?.length) && !role)}
              isLoading={record.isPending}
              onPress={() => record.mutate()}
            >
              {t('attempt.recordEquipment')}
            </ActionButton>
          </ActionBar>
          {refusal ? (
            <Alert tone="danger" title={t(`equipmentBlock.${refusal}`, { defaultValue: t('errors.CONFLICT') })} />
          ) : record.error ? (
            <ErrorNotice error={record.error} />
          ) : null}
        </div>
      ) : null}
    </Panel>
  );
}

/**
 * One submission gap in words. A general gap names its field; a specialised
 * gap may name a determination row (`determinations.<index>.<field>`) and
 * carries a code, so the reader learns which row, which reading and why.
 */
/** The i18n namespace that names each template's fields; D2216 is the default. */
const GAP_NAMESPACES: Readonly<Record<string, string>> = Object.freeze({
  [RESULT_TEMPLATE.ASTM_D2726_V1]: 'd2726',
  [RESULT_TEMPLATE.ASTM_D2041_V1]: 'd2041',
  [RESULT_TEMPLATE.AASHTO_T85_V1]: 't85',
  [RESULT_TEMPLATE.AASHTO_T96_V1]: 't96',
});

function describeGap(
  detail: { readonly field?: string | null; readonly code?: string | null },
  t: (key: string, options?: Record<string, unknown>) => string,
  resultPath: string,
): string {
  const field = detail.field ?? '';
  const problem = t(`fieldErrors.${detail.code ?? ''}`, { defaultValue: detail.code ?? '' });
  // Each template's rows and fields are named in its own words (D265, D274).
  const ns = GAP_NAMESPACES[resultPath] ?? 'd2216';
  const row = /^(determinations|specimens)\.(\d+)\.(\w+)$/.exec(field);
  if (row) {
    const rowName =
      ns === 'd2216'
        ? t('d2216.determinationNumbered', { number: Number(row[2]) + 1 })
        : t(`${ns}.rowNumbered`, { number: Number(row[2]) + 1 });
    return `${rowName} · ${t(`${ns}.field.${row[3]}`)}: ${problem}`;
  }
  const fraction = /^grading\.(\w+)$/.exec(field);
  if (fraction) {
    const sizes = T96_GRADING_FRACTIONS.find((candidate) => candidate.key === fraction[1]);
    const name = sizes ? t('t96.fraction', { passing: sizes.passing, retained: sizes.retained }) : field;
    return `${name}: ${problem}`;
  }
  if (ns !== 'd2216' && (field === 'specimens' || field === 'determinations')) {
    return detail.code && detail.code !== 'determinations_required'
      ? `${t(`${ns}.gap.${field}`)}: ${problem}`
      : t(`${ns}.gap.${field}`);
  }
  const known = t(`attempt.gap.${field}`, { defaultValue: '' });
  if (known) return known;
  const label = t(`${ns}.gap.${field}`, { defaultValue: t(`d2216.gap.${field}`, { defaultValue: field }) });
  return detail.code && detail.code !== 'required'
    ? `${label}: ${t(`fieldErrors.${detail.code}`, { defaultValue: detail.code })}`
    : label;
}

// ---------------------------------------------------------------------------
// Attachments — attached, never read
// ---------------------------------------------------------------------------

function AttachmentsPanel({
  attempt,
  isEditable,
  onChanged,
}: {
  readonly attempt: Attempt;
  readonly isEditable: boolean;
  readonly onChanged: (next: Attempt) => Promise<void>;
}) {
  const { t } = useTranslation();
  const { can } = useAuth();
  const locale = currentLocale();
  const [purpose, setPurpose] = useState<string | null>('worksheet');
  const [removing, setRemoving] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  const remove = useMutation({
    mutationFn: () => attemptsApi.removeAttachment(attempt.id, removing as string, reason.trim()),
    onSuccess: async (next) => {
      await onChanged(next);
      setRemoving(null);
      setReason('');
    },
  });

  return (
    <Panel title={t('attempt.attachmentsTitle')} description={t('attempt.attachmentsHint')}>
      {attempt.attachments.length === 0 ? (
        <p className="text-body text-default-600">{t('attempt.noAttachments')}</p>
      ) : (
        <ul className="flex flex-col gap-stack">
          {attempt.attachments.map((attachment) => (
            <li key={attachment.id} className="flex flex-wrap items-start gap-stack border-s-2 border-default-200 ps-3">
              <div className={`min-w-0 ${attachment.removedAt ? 'opacity-60' : ''}`}>
                <p className="truncate text-body font-medium text-default-900" dir="auto">
                  {attachment.logicalName}
                </p>
                <p className="text-caption text-default-600">
                  {t(`attachmentPurpose.${attachment.purpose}`)}
                  {' · '}
                  {/* Each part isolated, so a Latin name cannot split an
                      Arabic-formatted date beside it. */}
                  <bdi>{formatBytes(attachment.byteSize, { locale })}</bdi>
                  {' · '}
                  <bdi>{attachment.attachedBy.displayName}</bdi>
                  {' · '}
                  <bdi>{formatDateTime(attachment.attachedAt, { locale })}</bdi>
                </p>
                {attachment.note ? <p className="text-caption text-default-600">{attachment.note}</p> : null}
                {attachment.removedAt ? (
                  <p className="text-caption text-default-600">
                    {t('attempt.attachmentRemoved', { reason: attachment.removalReason ?? '' })}
                  </p>
                ) : null}
              </div>
              <div className="ms-auto flex items-center gap-inline">
                {can(PERMISSIONS.RESULTS_VIEW) ? (
                  <FileDownloadButton
                    fileId={attachment.fileId}
                    fileName={attachment.logicalName}
                    fetchContent={() => attemptsApi.downloadAttachment(attempt.id, attachment.id)}
                  />
                ) : null}
                {isEditable && !attachment.removedAt ? (
                  <ActionButton priority="tertiary" size="sm" onPress={() => setRemoving(attachment.id)}>
                    {t('common.remove')}
                  </ActionButton>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}

      {isEditable && can(PERMISSIONS.FILES_UPLOAD) ? (
        <div className="mt-stack flex flex-col gap-stack border-t border-default-100 pt-stack">
          <SelectField
            label={t('attempt.attachmentPurposeLabel')}
            value={purpose}
            onChange={setPurpose}
            options={ALL_ATTEMPT_ATTACHMENT_PURPOSES.map((value) => ({
              value,
              label: t(`attachmentPurpose.${value}`),
            }))}
          />
          <FileUploadField<Attempt>
            label={t('attempt.attachFile')}
            description={t('attempt.worksheetNotRead')}
            upload={(file) => attemptsApi.addAttachment(attempt.id, { file, purpose: purpose ?? 'other' })}
            onUploaded={onChanged}
          />
        </div>
      ) : null}

      <Dialog
        isOpen={removing !== null}
        onClose={() => setRemoving(null)}
        title={t('attempt.removeAttachmentTitle')}
        footer={
          <ActionBar>
            <ActionButton priority="tertiary" onPress={() => setRemoving(null)} isDisabled={remove.isPending}>
              {t('common.cancel')}
            </ActionButton>
            <ActionButton
              priority="primary"
              isDisabled={reason.trim().length < 3}
              isLoading={remove.isPending}
              onPress={() => remove.mutate()}
            >
              {t('common.remove')}
            </ActionButton>
          </ActionBar>
        }
      >
        <div className="flex flex-col gap-stack">
          <p className="text-body text-default-700">{t('attempt.removeAttachmentBody')}</p>
          <TextAreaField label={t('common.reason')} value={reason} onChange={setReason} isRequired rows={2} />
          {remove.error ? <ErrorNotice error={remove.error} /> : null}
        </div>
      </Dialog>
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// The preview — clearly an unissued draft, never a report
// ---------------------------------------------------------------------------

export function DraftPreview({ attempt }: { readonly attempt: Attempt }) {
  const { t } = useTranslation();
  const locale = currentLocale();
  const result = attempt.generalResult;
  const liveEquipment = attempt.equipment.filter((use) => !use.removedAt);
  const liveAttachments = attempt.attachments.filter((attachment) => !attachment.removedAt);

  return (
    <section
      aria-label={t('attempt.previewTitle')}
      className="relative overflow-hidden rounded-large border-2 border-dashed border-warning-400 bg-content1 p-gutter"
      data-testid="draft-preview"
    >
      <p className="mb-stack inline-block rounded-medium bg-warning-100 px-2 py-1 text-caption font-semibold uppercase tracking-wide text-warning-800">
        {attempt.status === 'draft' ? t('attempt.previewDraftMark') : t('attempt.previewSubmittedMark')}
      </p>
      <p className="mb-stack text-caption text-default-600">{t('attempt.previewNotAReport')}</p>
      <div className="grid grid-cols-1 gap-gutter sm:grid-cols-2">
        <ReadOnlyField label={t('attempt.summaryArLabel')}>
          <span dir="rtl" lang="ar">{result.summaryAr ?? t('common.notRecorded')}</span>
        </ReadOnlyField>
        <ReadOnlyField label={t('attempt.summaryEnLabel')}>
          <span dir="ltr" lang="en">{result.summaryEn ?? t('common.notRecorded')}</span>
        </ReadOnlyField>
        <ReadOnlyField label={t('attempt.generalNotesLabel')}>
          {result.generalNotes ?? t('common.notRecorded')}
        </ReadOnlyField>
        <ReadOnlyField label={t('attempt.executionLabel')}>
          {result.executionStartedAt
            ? formatDateTime(result.executionStartedAt, { locale })
            : t('common.notRecorded')}
          {' – '}
          {result.executionCompletedAt
            ? formatDateTime(result.executionCompletedAt, { locale })
            : t('common.notRecorded')}
        </ReadOnlyField>
        <ReadOnlyField label={t('attempt.equipmentTitle')}>
          {liveEquipment.length === 0
            ? t('common.notRecorded')
            : liveEquipment.map((use) => use.equipmentCode).join(', ')}
        </ReadOnlyField>
        <ReadOnlyField label={t('attempt.attachmentsTitle')}>
          {liveAttachments.length === 0
            ? t('common.notRecorded')
            : liveAttachments.map((attachment) => attachment.logicalName).join(', ')}
        </ReadOnlyField>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Submission — for review, not approval
// ---------------------------------------------------------------------------

function SubmitBar({
  attempt,
  onSubmitted,
  submitWith,
  onRefused,
}: {
  readonly attempt: Attempt;
  readonly onSubmitted: (next: Attempt) => Promise<void>;
  /** A specialised form submits its current values (D249). */
  readonly submitWith?: () => Promise<Attempt>;
  /** Told of a refusal, so the form can mark its fields; nothing is reset. */
  readonly onRefused?: (error: unknown) => void;
}) {
  const { t } = useTranslation();
  const [isConfirming, setConfirming] = useState(false);

  const submit = useMutation({
    mutationFn: () => (submitWith ? submitWith() : attemptsApi.submit(attempt.id)),
    onSuccess: async (next) => {
      setConfirming(false);
      await onSubmitted(next);
    },
    onError: (error) => onRefused?.(error),
  });

  const gaps =
    submit.error instanceof ApiError && submit.error.code === 'VALIDATION_FAILED'
      ? submit.error.details
      : [];

  return (
    <>
      <ActionBar>
        <ActionButton priority="primary" onPress={() => setConfirming(true)}>
          {t('attempt.submit')}
        </ActionButton>
      </ActionBar>
      <Dialog
        isOpen={isConfirming}
        onClose={() => setConfirming(false)}
        title={t('attempt.submit')}
        footer={
          <ActionBar>
            <ActionButton priority="tertiary" onPress={() => setConfirming(false)} isDisabled={submit.isPending}>
              {t('common.cancel')}
            </ActionButton>
            <ActionButton priority="primary" isLoading={submit.isPending} onPress={() => submit.mutate()}>
              {t('attempt.submitConfirm')}
            </ActionButton>
          </ActionBar>
        }
      >
        <div className="flex flex-col gap-stack">
          <Alert tone="info" title={t('attempt.submitNoticeTitle')} description={t('attempt.submitNoticeBody')} />
          {gaps.length > 0 ? (
            <Alert
              tone="danger"
              title={t('attempt.gapsTitle')}
              description={gaps.map((detail) => describeGap(detail, t, attempt.resultPath)).join('; ')}
            />
          ) : submit.error ? (
            <ErrorNotice error={submit.error} />
          ) : null}
        </div>
      </Dialog>
    </>
  );
}
